/**
 * Calculate the cost of newsletter add-ons
 * @param {Number} numNewsletterAddOns: Number of newsletter add-ons being purchased
 * @param {Number} pricePerNewsletterAddOn: Price per newsletter add-on
 * @returns {Number} The cost of newsletter add-ons
 */
const calculateNewsletterAddOnsSubtotal = (numNewsletterAddOns, pricePerNewsletterAddOn) => {
  return numNewsletterAddOns * pricePerNewsletterAddOn;
};

/**
 * Calculate the cost of press releases with the package discount, but before
 * any code discount
 * @param {Number} numPressReleases: Number of press releases being purchased
 * @param {Number} pricePerPressRelease: Price per press release after package discount
 * @returns {Number} The cost of press releases with package discount, but before any
 * code discount
 */
const calculatePressReleaseSubtotal = (numPressReleases, pricePerPressRelease) => {
  return numPressReleases * pricePerPressRelease;
};

/**
 * Calculate the subtotal of the cart accounting for all press releases and
 * newsletter add-ons
 * @param {Number} numPressReleases: Number of press releases being purchased
 * @param {Number} numNewsletterAddOns: Number of newsletter add-ons being purchased
 * @param {Number} pricePerPressRelease: Price per press release after package discount
 * @param {Number} pricePerNewsletterAddOn: Price per newsletter add-on
 * @param {Boolean} hasMultimediaAddOn: Indicates Multimedia add on addition if true
 * @param {Number} multimediaAddOnPrice: Price of Multimedia add on
 * @returns {Number} The cost of all press releases and newsletter add-ons
 */
const calculateCartSubtotal = (
  numPressReleases,
  numNewsletterAddOns,
  pricePerPressRelease,
  pricePerNewsletterAddOn,
  hasMultimediaAddOn,
  multimediaAddOnPrice,
) => {
  return (
    calculatePressReleaseSubtotal(numPressReleases, pricePerPressRelease)
    + calculateNewsletterAddOnsSubtotal(numNewsletterAddOns, pricePerNewsletterAddOn)
    + (hasMultimediaAddOn ? multimediaAddOnPrice : 0)
  );
};

/**
 * Calculate the discount from the coupon code/bulk purchase code based on the number of
 * press releases/newsletter add-ons the user is purchasing.
 * This is equal to the number of sites we're posting on * bulk purchase code
 * discount price
 * OR the lesser of the discount of the coupon code and
 * entire cart subtotal (press releases + newsletter add-ons)
 * However, if the code is a bulk purchase code, we must consider the number of
 * remaining credits and only apply the discount to the press releases that can be
 * covered by credits
 * @param {Number} numPressReleases: Number of press releases being purchased
 * @param {Number} pricePerPressRelease: Price per press release after package discount
 * @param {Number} cartSubtotal: Sum of cost of all items in the cart
 * @param {Object} checkoutCode: Coupon/Bulk Purchase Code applied to the "cart"
 */
const calculateCodeDiscount = (
  numPressReleases,
  pricePerPressRelease,
  cartSubtotal,
  checkoutCode,
) => {
  if (checkoutCode === null || checkoutCode.error) {
    return 0;
  }

  let codeDiscount;

  // The following camelcase variables are camelcased in the API
  // eslint-disable-next-line camelcase
  const {
    remaining_credits: remainingCredits,
    discount_amount: discountAmount,
    code_type: codeType,
  } = checkoutCode;

  switch (codeType) {
    case 'bulk_purchase_code': {
      // The number of times we will apply the discount is the lesser of
      // the code's remaining credits (if there are fewer credits than sites selected)
      // or the number of sites selected (if there are more credits than sites selected)
      // if they're equal, then it doesn't matter :)
      const creditsToConsume = Math.min(remainingCredits, numPressReleases);
      // The discount is equal to the number of credits we can apply * the discounted press release
      // price
      codeDiscount = (creditsToConsume * pricePerPressRelease);
      break;
    }
    case 'coupon_code': {
      // We apply the discount to the entire bundle of press releases.
      // If that would be greater than the cost of the press releases, then limit the discount
      // to that amount.
      codeDiscount = Math.min(discountAmount, cartSubtotal);
      break;
    }
    default:
      // if we somehow save a invalid code to state, we discount nothing
      codeDiscount = 0;
      break;
  }
  return codeDiscount;
};

/**
 * Calculate the total "cart" price of all press releases, with applicable
 * package discount and bulk purchase/coupon discount
 * @param {Number} numPressReleases: Number of press releases being purchased
 * @param {Number} numNewsletterAddOns: Number of newsletter add-ons being purchased
 * @param {Number} pricePerPressRelease: Price per press release after package discount
 * @param {Number} pricePerNewsletterAddOn: Price per newsletter add-on
 * @param {Object} checkoutCode: Coupon/Bulk Purchase Code applied to the "cart"
 * @param {Boolean} hasMultimediaAddOn: Indicates Multimedia add on addition if true
 * @param {Number} multimediaAddOnPrice: Price of Multimedia add on
 */
const calculateTotalPrice = (
  numPressReleases,
  numNewsletterAddOns,
  pricePerPressRelease,
  pricePerNewsletterAddOn,
  checkoutCode,
  hasMultimediaAddOn,
  multimediaAddOnPrice,
) => {
  // First, we calculate the discounted price with the package discount
  // This is equal to the number of sites we're posting on * the active package's
  // price_per_press_release
  const subtotal = calculateCartSubtotal(
    numPressReleases,
    numNewsletterAddOns,
    pricePerPressRelease,
    pricePerNewsletterAddOn,
    hasMultimediaAddOn,
    multimediaAddOnPrice,
  );

  // If there's a code, then determine how much we should discount from the total
  // based on number of press releases, the package-discounted price, and the
  // checkout code
  const codeDiscount = calculateCodeDiscount(
    numPressReleases,
    pricePerPressRelease,
    subtotal,
    checkoutCode,
  );

  return subtotal - codeDiscount;
};

export {
  calculateCartSubtotal,
  calculateCodeDiscount,
  calculateNewsletterAddOnsSubtotal,
  calculatePressReleaseSubtotal,
  calculateTotalPrice,
};
